/**
 * 2025 Tax Year Configuration
 *
 * Federal tax brackets, capital gains rates, and standard deductions.
 * Source: IRS Rev. Proc. 2024-40, as amended by the One Big Beautiful Bill
 * Act (OBBBA), which raised the 2025 standard deductions to $15,750 (single/
 * MFS), $31,500 (MFJ), and $23,625 (HoH).
 */

import { FederalTaxConfig } from '../../types.js';

export const federal2025: FederalTaxConfig = {
  brackets: {
    single: [
      { min: 0, max: 11925, rate: 0.10 },
      { min: 11925, max: 48475, rate: 0.12 },
      { min: 48475, max: 103350, rate: 0.22 },
      { min: 103350, max: 197300, rate: 0.24 },
      { min: 197300, max: 250525, rate: 0.32 },
      { min: 250525, max: 626350, rate: 0.35 },
      { min: 626350, max: Infinity, rate: 0.37 }
    ],
    marriedFilingJointly: [
      { min: 0, max: 23850, rate: 0.10 },
      { min: 23850, max: 96950, rate: 0.12 },
      { min: 96950, max: 206700, rate: 0.22 },
      { min: 206700, max: 394600, rate: 0.24 },
      { min: 394600, max: 501050, rate: 0.32 },
      { min: 501050, max: 751600, rate: 0.35 },
      { min: 751600, max: Infinity, rate: 0.37 }
    ],
    marriedFilingSeparately: [
      { min: 0, max: 11925, rate: 0.10 },
      { min: 11925, max: 48475, rate: 0.12 },
      { min: 48475, max: 103350, rate: 0.22 },
      { min: 103350, max: 197300, rate: 0.24 },
      { min: 197300, max: 250525, rate: 0.32 },
      { min: 250525, max: 375800, rate: 0.35 },
      { min: 375800, max: Infinity, rate: 0.37 }
    ],
    headOfHousehold: [
      { min: 0, max: 17000, rate: 0.10 },
      { min: 17000, max: 64850, rate: 0.12 },
      { min: 64850, max: 103350, rate: 0.22 },
      { min: 103350, max: 197300, rate: 0.24 },
      { min: 197300, max: 250500, rate: 0.32 },
      { min: 250500, max: 626350, rate: 0.35 },
      { min: 626350, max: Infinity, rate: 0.37 }
    ]
  },
  
  capitalGainsBrackets: {
    single: [
      { min: 0, max: 48350, rate: 0.00 },
      { min: 48350, max: 533400, rate: 0.15 },
      { min: 533400, max: Infinity, rate: 0.20 }
    ],
    marriedFilingJointly: [
      { min: 0, max: 96700, rate: 0.00 },
      { min: 96700, max: 600050, rate: 0.15 },
      { min: 600050, max: Infinity, rate: 0.20 }
    ],
    marriedFilingSeparately: [
      { min: 0, max: 48350, rate: 0.00 },
      { min: 48350, max: 300000, rate: 0.15 },
      { min: 300000, max: Infinity, rate: 0.20 }
    ],
    headOfHousehold: [
      { min: 0, max: 64750, rate: 0.00 },
      { min: 64750, max: 566700, rate: 0.15 },
      { min: 566700, max: Infinity, rate: 0.20 }
    ]
  },
  
  standardDeductions: {
    single: 15750,
    marriedFilingJointly: 31500,
    marriedFilingSeparately: 15750,
    headOfHousehold: 23625
  },
  
  niitThresholds: {
    single: 200000,
    marriedFilingJointly: 250000,
    marriedFilingSeparately: 125000,
    headOfHousehold: 200000
  },
  
  niitRate: 0.038,
  
  socialSecurityWageBase: 176100,
  socialSecurityRate: 0.062,
  
  medicareRate: 0.0145,
  additionalMedicareRate: 0.009,
  additionalMedicareThresholds: {
    single: 200000,
    marriedFilingJointly: 250000,
    marriedFilingSeparately: 125000,
    headOfHousehold: 200000
  },

  // AMT (Alternative Minimum Tax) - IRS Rev. Proc. 2024-40
  amtExemptions: {
    single: 88100,
    marriedFilingJointly: 137000,
    marriedFilingSeparately: 68500,
    headOfHousehold: 88100
  },
  amtPhaseOutThresholds: {
    single: 626350,
    marriedFilingJointly: 1252700,
    marriedFilingSeparately: 626350,
    headOfHousehold: 626350
  },
  amtPhaseOutRate: 0.25,
  // 26%/28% rate breakpoint (MFS is half of the general threshold)
  amtHighRateThreshold: {
    single: 239100,
    marriedFilingJointly: 239100,
    marriedFilingSeparately: 119550,
    headOfHousehold: 239100
  },
  amtRates: [0.26, 0.28],
  // Itemized deductions — OBBBA §70120: $40,000 SALT cap, reduced by 30% of
  // MAGI over $500,000 but not below $10,000 (MFS: half of each).
  itemizedDeductions: {
    saltCap: 40000,
    saltPhaseDownThreshold: 500000,
    saltPhaseDownRate: 0.30,
    saltFloor: 10000,
    charitableAgiFloorRate: 0,
    nonItemizerCharitableCap: { single: 0, marriedFilingJointly: 0 },
    itemizedBenefitLimitRate: 0
  },

  // IRS Notice 2024-80
  retirement401kLimit: 23500,
  retirement401kCatchup: 7500
};
